import pick from 'lodash/pick';
import { createSelector } from 'reselect';

import FeatureFlags from '../../constants/FeatureFlags';
import { PAY_NAME_BY_VALUE } from '../../constants/payby';
import { translate } from '../../i18n';
import { BillItem } from '../../types/BillItem';
import { ReviewStatus } from '../../types/Common';
import { ShippingLogistics } from '../../types/ShippingLogistics';
import type {
  Product,
  TransactionDetailData,
} from '../../types/TransactionDetail';
import isExists from '../../utils/isExists';
import { RootState } from '../rootReducer';

const orderByIdReducerRaw = (state: RootState) => state?.orderById || {};

export const makeOrderDataById = orderId => {
  return createSelector(orderByIdReducerRaw, data => {
    return data?.[orderId]?.data || {};
  });
};

export const makeOrderDataByIdByKey = (orderId: string, key: string) => {
  return createSelector(orderByIdReducerRaw, data => {
    return data?.[orderId]?.[key];
  });
};

export const makeIsOrderLoadingById = orderId => {
  return createSelector(orderByIdReducerRaw, data => {
    const isLoading = data?.[orderId]?.isLoading;

    return isExists(isLoading) ? isLoading : true;
  });
};

export const makeLoadingTypeById = orderId => {
  return createSelector(orderByIdReducerRaw, data => {
    const loadingType = data?.[orderId]?.loadingType;

    return loadingType;
  });
};

// 找不到的時候，不適合有 default，會造成 type error
export const makeOrderDataByNameById = <K extends keyof TransactionDetailData>(
  orderId: string,
  name: K
) => {
  return createSelector(
    orderByIdReducerRaw,
    data => data?.[orderId]?.data?.[name]
  );
};

export const makeReviewStatus = orderId => {
  return createSelector(orderByIdReducerRaw, data => {
    const products =
      data?.[orderId]?.data?.product_data?.shipped.flatMap(
        packet => packet.products
      ) || [];

    for (const item of products) {
      /**
       * 商品評價狀態有
       * - Disabled: 不可評價
       * - NotReviewed: 尚未評價
       * - Done: 已評價
       *
       * 目前評價的操作是以訂單為單位，
       * 這邊取第一個評價狀態不為`不可評價`的商品作為訂單評價狀態
       *
       * 其他地方如訂單列表也採用相同邏輯
       * selectors/transactionList.ts -> makeTransactionList
       */
      if (item.review_status !== ReviewStatus.Disabled) {
        return item.review_status;
      }
    }

    return ReviewStatus.Disabled;
  });
};

export const makeTransactionStatusById = orderId => {
  return createSelector(
    makeOrderDataByNameById(orderId, 'transaction_status'),
    data => data || {}
  );
};

export const makeIsSeparatedById = orderId => {
  return createSelector(
    makeOrderDataById(orderId),
    data => data?.product_data?.is_separated || false
  );
};

export const makeReviewDeadlineById = orderId => {
  return createSelector(
    makeOrderDataById(orderId),
    data => data?.review_deadline
  );
};

export const makeOrderPaymentById = orderId => {
  return createSelector(
    makeOrderDataByNameById(orderId, 'transaction_no'),
    makeOrderDataByNameById(orderId, 'transaction_status'),
    makeOrderDataByNameById(orderId, 'transaction_hint'),
    makeOrderDataByNameById(orderId, 'created_at'),
    makeOrderDataByNameById(orderId, 'payment_data'),
    (transactionNo, transactionStatus, transactionHint, createdAt, data) => {
      // Object.keys([]).length => 0
      // Object.getOwnPropertyNames([]).length => 1
      // Object.keys({}).length => 0
      // Object.getOwnPropertyNames('').length => 1
      if (!data || Object.getOwnPropertyNames(data).length === 0) {
        return null;
      }

      const PAY_NAME = PAY_NAME_BY_VALUE[data.pay_by];

      return {
        PAY_NAME,
        transaction_no: transactionNo,
        transaction_status: transactionStatus,
        transaction_hint: transactionHint,
        created_at: createdAt,
        ...data,
      };
    }
  );
};

export const makeOrderDeliveryById = orderId => {
  return createSelector(
    makeOrderDataByNameById(orderId, 'delivery_data'),
    data => data || {}
  );
};

export const makeOrdersLatestLogisticsProgressById = (orderId, packageNum?) => {
  if (isExists(packageNum)) {
    return createSelector(
      makeOrderDataByNameById(orderId, 'product_data'),
      productData => {
        const shippedArray = productData?.shipped || [];
        const shipped = shippedArray.find(
          data => data.package_num === packageNum
        );

        return shipped?.latest_logistics_progress || {};
      }
    );
  }
  // 邏輯：優先取得最新（index 最大）的出貨單的出貨狀態，且未出貨優先於已出貨

  return createSelector(
    makeOrderDataByNameById(orderId, 'product_data'),
    data => {
      if (!data) {
        return {};
      }

      return (
        (data.unshipped.length > 0
          ? data.unshipped[data.unshipped.length - 1].latest_logistics_progress
          : data.shipped[data.shipped.length - 1].latest_logistics_progress) ||
        {}
      );
    }
  );
};

export const makeOrderProductsById = orderId => {
  return createSelector(
    makeOrderDataByNameById(orderId, 'product_data'),
    data => {
      if (!data) {
        return [];
      }

      const shippedProducts: Product[] = data.shipped.flatMap(
        packet => packet.products
      );

      const unshippedProducts: Product[] = data.unshipped.flatMap(
        packet => packet.products
      );
      const products = [...shippedProducts, ...unshippedProducts];

      return products;
    }
  );
};

export const makeOrderShippedProductsById = orderId => {
  return createSelector(
    makeOrderDataByNameById(orderId, 'product_data'),
    data => data?.shipped || []
  );
};

export const makeOrderUnShippedProductsById = orderId => {
  return createSelector(
    makeOrderDataByNameById(orderId, 'product_data'),
    data => data?.unshipped || []
  );
};

export const makeOrderInvoiceById = orderId => {
  return createSelector(
    makeOrderDataByNameById(orderId, 'invoice_data'),
    data => data || {}
  );
};

export const makeOrderCurrencyById = orderId => {
  return createSelector(
    makeOrderDataByNameById(orderId, 'payment_data'),
    data => data?.currency
  );
};

interface AttributeInfo {
  where: string;
  value: any;
  i18nKey: string;
  showValue: Function;
}
const attributeInfo = {
  subtotal: {
    where: 'product_data',
    value: data => data?.subtotal_price,
    i18nKey: 'subtotal',
    showValue: value => isExists(value),
  },
  shippingFee: {
    where: 'delivery_data',
    value: data => data?.shipping_fee,
    i18nKey: FeatureFlags.CX_2444_OVERSEA_SHIPPING_FEE
      ? 'internalShippingFee'
      : 'shippingFee',
    showValue: value => isExists(value),
  },
  crossBorderShippingFee: {
    where: 'delivery_data',
    value: data => data?.cross_border_shipping_fee,
    i18nKey: 'crossBorderShippingFee',
    showValue: value => isExists(value),
  },
  paymentFee: {
    where: 'payment_data',
    value: data => data?.payment_fee,
    i18nKey: 'paymentFee',
    showValue: value => isExists(value) && !!value,
  },
  useVcoin: {
    where: 'payment_data',
    value: data => data?.use_vcoin,
    i18nKey: 'vcoin-bill-detail',
    showValue: value => isExists(value) && !!value,
  },
  useEventVcoin: {
    where: 'payment_data',
    value: data => data?.use_event_vcoin,
    i18nKey: 'event-vcoin-bill-detail',
    showValue: value => isExists(value) && !!value,
  },
  useStorageVcoin: {
    where: 'payment_data',
    value: data => data?.use_storage_vcoin,
    i18nKey: 'storage-vcoin-bill-detail',
    showValue: value => isExists(value) && !!value,
  },
  useCouponValue: {
    where: 'payment_data',
    value: data => data?.use_coupon_value,
    i18nKey: 'coupon-bill-detail',
    showValue: value => isExists(value) && !!value,
  },
  totalPrice: {
    where: 'payment_data',
    value: data => data?.total_price,
    i18nKey: 'totalPrice',
    showValue: value => isExists(value),
  },
};

export const makeOrderAmountDetailById = orderId => {
  return createSelector(makeOrderDataById(orderId), data => {
    const priceInfo = [
      'subtotal',
      'useCouponValue',
      'useEventVcoin',
      'useStorageVcoin',
      'shippingFee',
      'paymentFee',
      'totalPrice',
    ];

    if (FeatureFlags.CX_2444_OVERSEA_SHIPPING_FEE) {
      priceInfo.splice(3, 0, 'crossBorderShippingFee');
    }

    return priceInfo.reduce((result: BillItem[], labelName: string) => {
      const config = attributeInfo[labelName] as AttributeInfo;
      const value: number = config.value(data[config.where]);

      if (config.showValue(value)) {
        result.push({
          labelName,
          label: translate(config.i18nKey),
          value,
        });
      }

      return result;
    }, []);
  });
};

interface ShippingLogisticsData
  extends Omit<ShippingLogistics, 'is_hidden' | 'transaction_no'> {
  transaction_no?: string;
}

export const makeShippingLogisticsData = (
  transactionID: string,
  packageNum: number = 0
) => {
  return createSelector(orderByIdReducerRaw, rawData => {
    const data =
      rawData?.[transactionID]?.shippingLogistics?.[packageNum] || {};

    const result: ShippingLogisticsData = pick(data, [
      'delivery_mode',
      'shipping_logistics_progress',
      'local_ship_no',
      'logistics_provider',
      'deadline_delivery_date',
    ]);

    result.transaction_no = data.transaction_no || transactionID;

    return result || {};
  });
};

export const makeShippingLogisticsProgress = (
  transactionID: string,
  packageNum: number = 0
) => {
  return createSelector(
    makeShippingLogisticsData(transactionID, packageNum),
    data => {
      const progress = data?.shipping_logistics_progress || [];

      return [...progress].reverse();
    }
  );
};
